import React, { useState, useEffect, useRef } from 'react';
import { 
  ArrowRight, 
  Check, 
  Building2, 
  Phone, 
  Smartphone,
  ShieldCheck,
  Compass,
  TrendingUp,
  Play
} from 'lucide-react';
import { 
  SquigglyUnderline
} from '../components/Doodles';

const Landing = () => {
  useEffect(() => {
    document.title = 'Sparklyt';
  }, []);

  const [openFaq, setOpenFaq] = useState(null);
  const [isVideoPlaying, setIsVideoPlaying] = useState(false);
  const videoRef = useRef(null);

  const handlePlayVideo = () => {
    if (videoRef.current) {
      videoRef.current.play();
      setIsVideoPlaying(true);
    }
  };

  const toggleFaq = (index) => {
    setOpenFaq(openFaq === index ? null : index);
  };

  const faqs = [
    {
      q: 'Does my child need to already be good at something?',
      a: 'No. It works just as well, and often better, for a student who has no idea yet. Starting without fixed expectations means they do not have to unlearn anything.',
    },
    {
      q: 'How much time does this take each week?',
      a: 'During the first two months, a few minutes a day in the app. After that, weekend experiences happen about two to three times a month. It sits easily alongside school and coaching instead of competing with them.',
    },
    {
      q: 'What if my child does not like any of it?',
      a: 'That is a clear and useful answer. Finding out at sixteen that a field is not for them takes a few weekends. Finding that out at twenty-two costs years.',
    },
    {
      q: 'Is this meant to replace coaching?',
      a: 'No. Coaching helps a student reach a specific goal. Sparklyt helps families figure out where to point them in the first place.',
    },
    {
      q: 'What happens to what my child writes in the app?',
      a: 'It belongs entirely to your child. We do not sell or share their data. Parents receive the summary reports and progress updates, while personal reflections stay private.',
    },
    {
      q: 'What happens after the free two months?',
      a: 'You get a detailed report and a conversation with our team to see if the offline stage is right for your family. If not, you keep the report and there is no obligation to continue.',
    },
  ];

  return (
    <div className="landing-page" id="top">
      {/* 1. HERO SECTION */}
      <section className="hero-section wrap min-h-[calc(100dvh-64px)] sm:min-h-[calc(100vh-68px)] flex flex-col justify-center py-10 sm:py-16 md:py-24">
        <div className="max-w-[800px] my-auto">
          {/* Hero Heading */}
          <h1 className="hero-heading">
            Most students choose a path they have{' '}
            <span className="relative inline-block text-[#1A1917]">
              never actually tried
              <SquigglyUnderline className="text-[#C07A22] h-2.5 sm:h-3 w-full -mt-0.5 sm:-mt-1" />
            </span>
            .
          </h1>

          {/* Hero Subtitle */}
          <p className="hero-subtext">
            Sparklyt helps a family find out what their child is drawn to by letting them try the actual work before committing years to it.
          </p>

          {/* Hero Action */}
          <div className="hero-actions">
            <a
              href="https://app.sparklyt.com"
              className="spark-btn lg group w-full sm:w-auto text-center"
            >
              Start free
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </a>
            <span className="text-[14.5px] sm:text-[15px] font-medium text-[#635F58]">
              The first two months are free.
            </span>
          </div>
        </div>
      </section>

      {/* 2. THE OLD WAY */}
      <section className="sec sec--line wrap">
        <div className="max-w-[760px]">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[13px] font-bold tracking-wider uppercase text-[#C24128]">
              The Usual Approach
            </span>
          </div>
          <h2>How it usually gets decided</h2>
          
          <ul className="old-list">
            <li className="old-item">
              <span className="old-mark">✕</span>
              <span>A two-hour aptitude test</span>
            </li>
            <li className="old-item">
              <span className="old-mark">✕</span>
              <span>An uncle's opinion</span>
            </li>
            <li className="old-item">
              <span className="old-mark">✕</span>
              <span>Whatever the marks allow</span>
            </li>
            <li className="old-item">
              <span className="old-mark">✕</span>
              <span>Whichever coaching centre has an open seat</span>
            </li>
            <li className="old-item">
              <span className="old-mark">✕</span>
              <span>Whatever the neighbour's son did</span>
            </li>
          </ul>

          <p className="old-note">
            Not one of these asks the only question that matters:{' '}
            <em className="italic text-[#1A1917]">
              Have you ever actually done it?
            </em>
          </p>

          <p className="lede">
            None of this is a parent's fault. We were all handed the same map, and it was drawn for a world that no longer exists.
          </p>
        </div>
      </section>

      {/* 3. WHAT IT IS */}
      <section className="sec sec--wash">
        <div className="wrap">
          <div className="max-w-[840px]">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[13px] font-bold tracking-wider uppercase text-[#C07A22]">
                WHAT THIS IS
              </span>
            </div>
            <h2>What Sparklyt is</h2>
            
            <p className="lede">
              A twelve-month journey for your family. Your child explores many different fields, goes out and tries the ones that pull them inside real workplaces, and builds something in whatever survives.
            </p>

            <div className="mt-8 pl-5 sm:pl-6 border-l-2 border-[#C07A22] py-1">
              <p className="text-[19px] sm:text-[22px] font-bold text-[#1A1917] leading-[1.45] tracking-tight max-w-[38ch]">
                No test decides this. No counsellor decides this. What your child actually does decides it.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* 4. VIDEO WALKTHROUGH */}
      <section className="sec sec--line wrap" id="walkthrough">
        <div className="max-w-[840px]">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[13px] font-bold tracking-wider uppercase text-[#C07A22]">
              Walkthrough
            </span>
          </div>
          <h2>How to use Sparklyt</h2>
          <p className="lede">
            A quick 5-minute walkthrough of the Sparklyt app and what daily discovery looks like for your child.
          </p>

          <div className="mt-8 bg-[#1A1917] rounded-[2px] overflow-hidden border border-[#E5E2DA] shadow-xl relative group">
            <video
              ref={videoRef}
              src="https://res.cloudinary.com/dmyylnwj8/video/upload/v1789418845/overview_j9ryno.mp4"
              className={`w-full h-auto aspect-video object-cover transition-all duration-500 ${!isVideoPlaying ? 'blur-sm scale-[1.02]' : ''}`}
              controls={isVideoPlaying}
              preload="auto"
              playsInline
              onPlay={() => setIsVideoPlaying(true)}
              onPause={() => {
                if (videoRef.current && videoRef.current.paused && videoRef.current.currentTime === 0) {
                  setIsVideoPlaying(false);
                }
              }}
            >
              <source src="https://res.cloudinary.com/dmyylnwj8/video/upload/v1789418845/overview_j9ryno.mp4" type="video/mp4" />
              Your browser does not support the video tag.
            </video>

            {!isVideoPlaying && (
              <div 
                onClick={handlePlayVideo}
                className="absolute inset-0 bg-black/40 backdrop-blur-[3px] flex flex-col items-center justify-center cursor-pointer transition-all duration-300 group-hover:bg-black/30"
              >
                {/* Floating Play Button */}
                <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#C07A22] hover:bg-[#A76618] text-white flex items-center justify-center shadow-2xl transform transition-transform duration-300 group-hover:scale-110">
                  <Play size={30} className="ml-1 fill-white" />
                </div>

                <div className="mt-4 text-center px-4">
                  <p className="text-white text-[16px] sm:text-[18px] font-bold drop-shadow-md">
                    Watch how Sparklyt works
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      {/* 5. THE JOURNEY (HOW THE YEAR WORKS) */}
      <section className="sec sec--wash" id="how-it-works">
        <div className="wrap">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[13px] font-bold tracking-wider uppercase text-[#C07A22]">
              The 12-Month Plan
            </span>
          </div>
          <h2>How the year works</h2>

        <div className="timeline-container">
          {/* Stage 1 */}
          <div className="timeline-stage">
            <span className="dur-badge">Stage 01 · Two months</span>
            <h3>First, we find out what pulls them.</h3>
            <p>
              Your child spends two months in the Sparklyt app, doing short daily activities across building, business, science, design, finance and media. No tests, no rankings. We simply watch what they keep returning to when nobody is grading them.
            </p>
            <div className="outcome-box">
              You get a clear report.
              <span>Not a personality label. A look at what they gravitated toward and what they quietly avoided.</span>
            </div>
          </div>

          {/* Stage 2 */}
          <div className="timeline-stage">
            <span className="dur-badge">Stage 02 · Four months</span>
            <h3>Then we see if it holds up to real work.</h3>
            <p>
              Three fields, explored weekend by weekend in the real world. Inside a working startup, a day at an accelerator, a real service floor, a research lab. We include the routine and boring parts on purpose, because that is where the truth comes out.
            </p>
            <div className="outcome-box">
              Three fields narrow to one.
              <span>Some students discover they liked the idea of a job, but not the daily reality. Finding that out at sixteen is the goal.</span>
            </div>
          </div>

          {/* Stage 3 */}
          <div className="timeline-stage">
            <span className="dur-badge">Stage 03 · Six months</span>
            <h3>Then they build something real.</h3>
            <p>
              Whatever field survives gets focused attention. Your child works with a mentor who actually does this work, takes on real projects, and contributes through an internship. Everything they make is recorded along the way.
            </p>
            <div className="outcome-box">
              They finish with a portfolio.
              <span>A concrete record of what they have actually built, and what they learned doing it.</span>
            </div>
          </div>
        </div>
      </div>
    </section>

      {/* 5. ONLINE + OFFLINE DUO */}
      <section className="sec sec--line wrap" id="real-world">
        <div className="flex items-center gap-2 mb-3">
          <span className="text-[13px] font-bold tracking-wider uppercase text-[#C07A22]">
            HOW THEY FIT TOGETHER
          </span>
        </div>
        <h2>The app and the real world do different jobs</h2>

        <div className="duo-grid">
          <div className="duo-card">
            <h4>
              <span>In the app</span>
              <Smartphone size={22} className="text-[#C07A22]" />
            </h4>
            <p>
              Where discovery begins and experiences get recorded. Two months of initial exploration, followed by a journal of every real-world project that builds into their portfolio. Free to start, and your child owns everything in it.
            </p>
          </div>

          <div className="duo-card">
            <h4>
              <span>In the real world</span>
              <Building2 size={22} className="text-[#C07A22]" />
            </h4>
            <p>
              Where ideas get tested. Weekends spent inside places families rarely access on their own, like active startups, accelerators, labs, and studios. Sparklyt arranges the access, and your child does the work.
            </p>
          </div>
        </div>

        <p className="lede mt-10">
          One feeds the other. The app shows us where to point them. The real world shows us if it holds.
        </p>
      </section>

      {/* 6. WHAT YOU GET */}
      <section className="sec sec--wash" id="what-you-get">
        <div className="wrap">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[13px] font-bold tracking-wider uppercase text-[#C07A22]">
              What You Get
            </span>
          </div>
          <h2>What your child walks away with</h2>

          <ul className="gets-list">
            <li className="gets-item">
              <div className="flex items-start gap-3">
                <Check size={20} className="text-[#C07A22] mt-0.5 flex-shrink-0" />
                <div>
                  A clearer picture of what genuinely pulls them
                  <span>Built through exploration and real experiences, rather than a single test.</span>
                </div>
              </div>
            </li>
            <li className="gets-item">
              <div className="flex items-start gap-3">
                <Check size={20} className="text-[#C07A22] mt-0.5 flex-shrink-0" />
                <div>
                  Hands-on experience in three different fields
                  <span>Not a talk about the work, but the work itself.</span>
                </div>
              </div>
            </li>
            <li className="gets-item">
              <div className="flex items-start gap-3">
                <Check size={20} className="text-[#C07A22] mt-0.5 flex-shrink-0" />
                <div>
                  Conversations with people who do the work
                  <span>Hear what the field actually looks like from people inside it.</span>
                </div>
              </div>
            </li>
            <li className="gets-item">
              <div className="flex items-start gap-3">
                <Check size={20} className="text-[#C07A22] mt-0.5 flex-shrink-0" />
                <div>
                  A portfolio of real work
                  <span>Real work they can keep, reflect on and build on.</span>
                </div>
              </div>
            </li>
            <li className="gets-item">
              <div className="flex items-start gap-3">
                <Check size={20} className="text-[#C07A22] mt-0.5 flex-shrink-0" />
                <div>
                  A decision backed by evidence
                  <span>Along with the ability to explain clearly why they chose it.</span>
                </div>
              </div>
            </li>
          </ul>
        </div>
      </section>

      {/* 7. WHY THIS IS NOT A CAREER TEST */}
      <section className="sec sec--line wrap">
        <div className="max-w-[800px]">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[13px] font-bold tracking-wider uppercase text-[#C07A22]">
              Evidence Over Quizzes
            </span>
          </div>
          <h2>Why this is not a career test</h2>

          <div className="diff-card">
            <p className="diff-title">
              A test asks what your child thinks they might like. Sparklyt gives them opportunities to find out.
            </p>
            <p className="diff-body">
              Every exploration leaves something behind: a project they chose to continue, a conversation that changed their thinking, something they struggled with, or a field they kept coming back to. Over time, these experiences create a clearer picture of what actually fits. That is Sparklyt.
            </p>
            <div className="mt-5 pt-4 border-t border-[#E5E2DA] text-[14.5px] font-semibold text-[#1A1917]">
              You can see the evidence behind every insight.
            </div>
          </div>
        </div>
      </section>

      {/* 8. WHO THIS IS FOR */}
      <section className="sec sec--wash" id="who-this-is-for">
        <div className="wrap">
          <div className="max-w-[760px]">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[13px] font-bold tracking-wider uppercase text-[#C07A22]">
                Audience
              </span>
            </div>
            <h2>Who this is for</h2>
            
            <p className="text-[20px] sm:text-[24px] font-bold text-[#1A1917] mt-4">
              Students in Classes 8-12 and their parents.
            </p>

            <p className="text-[14.5px] font-medium text-[#C07A22] mt-1">
              Currently starting with families in Bengaluru.
            </p>

            <p className="lede mt-4">
              Parents are part of the process too. The conversations that matter most often continue at home.
            </p>
          </div>
        </div>
      </section>

      {/* 9. WHAT PARENTS WALK AWAY WITH */}
      <section className="sec sec--line wrap">
        <div className="max-w-[840px]">
            <div className="flex items-center gap-2 mb-3">
              <span className="text-[13px] font-bold tracking-wider uppercase text-[#C07A22]">
                For Parents
              </span>
            </div>
            <h2>What parents walk away with</h2>

            <div className="parent-takeaways-grid">
              <div className="parent-takeaway-card">
                <div className="parent-takeaway-header">
                  <div className="parent-takeaway-icon">
                    <ShieldCheck size={20} className="text-[#C07A22]" />
                  </div>
                  <h4>A body of evidence you can trust</h4>
                </div>
                <p>
                  A structured record of what your child explored, experienced and created - so their interests don't have to rely on “I think I like this.”
                </p>
              </div>

              <div className="parent-takeaway-card">
                <div className="parent-takeaway-header">
                  <div className="parent-takeaway-icon">
                    <Compass size={20} className="text-[#C07A22]" />
                  </div>
                  <h4>More confidence when the decision gets closer</h4>
                </div>
                <p>
                  You don't have to make a high-stakes decision based on a conversation at the edge of Class 12.
                </p>
              </div>

              <div className="parent-takeaway-card">
                <div className="parent-takeaway-header">
                  <div className="parent-takeaway-icon">
                    <TrendingUp size={20} className="text-[#C07A22]" />
                  </div>
                  <h4>Room to think beyond the immediate ROI</h4>
                </div>
                <p>
                  When you understand what your child is actually drawn to, career decisions don't have to begin and end with the quickest financial return.
                </p>
              </div>
            </div>
          </div>
        </section>

      {/* 10. FOUNDER SECTION */}
      <section className="sec sec--line wrap" id="founder">
        <div className="max-w-[760px]">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[13px] font-bold tracking-wider uppercase text-[#C07A22]">
              Founder's Story
            </span>
          </div>
          <h2>Who is behind this</h2>

          <div className="founder-box">
            <p>
              I built Sparklyt because I went through this process the hard way. I picked a direction at sixteen based on almost nothing, spent years realising it was not for me, and eventually found what was, long after it would have been helpful to know.
            </p>
            <p>
              Nobody ever asked what I was naturally good at. They only asked what I scored. That is nobody's fault, but we can do better now. I would rather build what I needed back then than complain that it did not exist.
            </p>
            <p>
              We are in our early days. Our first families are directly shaping how this grows, and we will always be upfront about that.
            </p>

            <div className="founder-sig">
              <div>
                Pranav
                <span>Founder, Sparklyt · Bengaluru</span>
              </div>
              <a
                href="tel:8904145446"
                className="text-[14px] text-[#C07A22] hover:underline font-semibold flex items-center gap-1.5"
              >
                <Phone size={14} />
                8904145446
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* 11. FAQ ACCORDION */}
      <section className="sec sec--wash wrap" id="faq">
        <div className="max-w-[760px]">
          <div className="flex items-center gap-2 mb-3">
            <span className="text-[13px] font-bold tracking-wider uppercase text-[#C07A22]">
              Questions & Answers
            </span>
          </div>
          <h2>Questions families ask</h2>

          <div className="faq-list">
            {faqs.map((faq, index) => {
              const isOpen = openFaq === index;
              return (
                <div key={index} className="faq-item">
                  <button
                    onClick={() => toggleFaq(index)}
                    className="faq-button"
                    aria-expanded={isOpen}
                  >
                    <span>{faq.q}</span>
                    <span className={`faq-icon ${isOpen ? 'rotate-180 bg-[#C07A22] text-white' : ''}`}>
                      {isOpen ? '–' : '+'}
                    </span>
                  </button>
                  {isOpen && (
                    <div className="faq-answer animate-in fade-in-50 duration-200">
                      <p>{faq.a}</p>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* 12. FINAL CTA SECTION */}
      <section className="final-section wrap" id="start">
        <div className="max-w-[760px]">
          <h2 className="text-[34px] sm:text-[46px] leading-[1.08] tracking-[-0.035em] font-extrabold text-[#1A1917]">
            Start with two months. See what your child discovers.
          </h2>
          <p className="text-[16.5px] sm:text-[18.5px] text-[#635F58] mt-3 max-w-[54ch]">
            At the end, you'll receive a report built from their exploration, experiences and reflections.
          </p>

          <div className="hero-actions">
            <a
              href="https://app.sparklyt.com"
              className="spark-btn lg group"
            >
              Start free
              <ArrowRight size={17} className="transition-transform group-hover:translate-x-1" />
            </a>
            <span className="text-[15px] font-medium text-[#635F58]">
              Coming soon on the Play Store.
            </span>
          </div>

          <div className="mt-8 pt-6 border-t border-[#E5E2DA] flex items-center gap-3 text-[14.5px] text-[#635F58]">
            <Phone size={15} className="text-[#C07A22]" />
            <span>Prefer to talk first? Call or WhatsApp Pranav directly at <strong>8904145446</strong></span>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Landing;
